import { useSyncExternalStore } from "react";
import { Cookies } from "react-cookie";
import { decodeToken } from "react-jwt";

export const AUTH_COOKIE = "Authorization";

const USER_AUTH_PREFIXES = ["CO::", "GO::", "LI::"];

type AuthorizationListener = () => void;

function getCookies(): Cookies {
	return new Cookies();
}

export function getAuthorizationToken(): string | null {
	const token = getCookies().get(AUTH_COOKIE);
	return typeof token === "string" && token ? token : null;
}

export function hasUserAuthPrefix(token: string | null = getAuthorizationToken()): boolean {
	return USER_AUTH_PREFIXES.some((prefix) => token?.startsWith(prefix));
}

export function stripAuthorizationPrefix(token: string | null = getAuthorizationToken()): string | null {
	if (!token) return null;

	for (const prefix of USER_AUTH_PREFIXES) {
		if (token.startsWith(prefix)) {
			return token.slice(prefix.length);
		}
	}

	return token;
}

export function getAuthorizationTokenExpiration(token: string | null = getAuthorizationToken()): Date | null {
	const decodedToken = decodeToken<{ exp?: number }>(stripAuthorizationPrefix(token) || "");
	if (!decodedToken?.exp) {
		return null;
	}

	return new Date(decodedToken.exp * 1000);
}

export function decodeClaimAuthorizationToken(token: string | null = getAuthorizationToken()) {
	if (!token) {
		return null;
	}

	try {
		const decodedToken = decodeToken<{ data?: { review_token?: string } }>(token);
		const reviewToken = decodedToken?.data?.review_token;
		const claimPayload = typeof reviewToken === "string" ? JSON.parse(reviewToken) : null;

		if (!claimPayload?.companyId) {
			return null;
		}

		return {
			...claimPayload,
			expiresAt: getAuthorizationTokenExpiration(token)?.toISOString() || null,
		};
	} catch {
		return null;
	}
}

export function isClaimAuthorizationToken(token: string | null = getAuthorizationToken()): boolean {
	return Boolean((decodeClaimAuthorizationToken(token) as { companyId?: string } | null)?.companyId);
}

export function isUserAuthorizationToken(token: string | null = getAuthorizationToken()): boolean {
	return Boolean(token) && hasUserAuthPrefix(token) && !isClaimAuthorizationToken(token);
}

export function isUserAuthorizationExpired(token: string | null = getAuthorizationToken()): boolean {
	if (!isUserAuthorizationToken(token)) {
		return true;
	}

	if (token.startsWith("LI::")) {
		return false;
	}

	try {
		const decodedToken = decodeToken<{ exp?: number }>(stripAuthorizationPrefix(token) || "");
		return !decodedToken?.exp || decodedToken.exp <= Date.now() / 1000;
	} catch {
		return true;
	}
}

export function clearAuthorizationToken() {
	getCookies().remove(AUTH_COOKIE, {
		path: "/",
	});
	notifyAuthorizationChange();
}

export function setAuthorizationToken(token: string, expiresAt?: string | number | Date) {
	if (!token) {
		return;
	}

	const expirationDate = expiresAt
		? expiresAt instanceof Date
			? expiresAt
			: new Date(expiresAt)
		: getAuthorizationTokenExpiration(token);

	getCookies().remove(AUTH_COOKIE, { path: "/" });
	getCookies().set(AUTH_COOKIE, token, {
		path: "/",
		...(expirationDate ? { expires: expirationDate } : {}),
	});
	notifyAuthorizationChange();
}

/* -------------------------------------------------------------------------------------------- *
 * Reactive credential
 *
 * The credential used to be read straight from the cookie inside render. Nothing re-rendered when
 * the cookie appeared, so queries and route guards that resolved "signed out" on first render
 * stayed stale for the life of the page.
 *
 * One store owns the credential now, and every reader subscribes to it.
 * -------------------------------------------------------------------------------------------- */

const listeners = new Set<AuthorizationListener>();

/** Call after any write to the cookie so subscribed components re-render. */
function notifyAuthorizationChange() {
	for (const listener of listeners) listener();
}

export function subscribeToAuthorization(listener: AuthorizationListener): () => void {
	listeners.add(listener);

	// A credential can also change outside this tab — a sign-out elsewhere, or devtools. Re-reading
	// when the tab regains focus catches those without polling the cookie on a timer.
	const recheck = () => listener();
	if (typeof window !== "undefined") window.addEventListener("focus", recheck);

	return () => {
		listeners.delete(listener);
		if (typeof window !== "undefined") window.removeEventListener("focus", recheck);
	};
}

/** Snapshot is a string (or null), so React's identity check is a value comparison. */
function readAuthorizationToken(): string | null {
	return getAuthorizationToken();
}

/**
 * The current credential, re-rendering the caller whenever it changes.
 *
 * Use this instead of calling `getAuthorizationToken()` during render.
 */
export function useAuthorizationToken(): string | null {
	return useSyncExternalStore(
		subscribeToAuthorization,
		readAuthorizationToken,
		// Server render has no cookie jar.
		() => null,
	);
}

/** True when the credential identifies a signed-in user and has not expired. */
export function hasActiveUserAuthorization(token: string | null = getAuthorizationToken()): boolean {
	return isUserAuthorizationToken(token) && !isUserAuthorizationExpired(token);
}
